import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import { 
  ArrowLeft, 
  Download, 
  FileText, 
  BookOpen, 
  Calendar, 
  Eye,
  Printer,
  Archive,
  GraduationCap,
  Clock,
  Loader,
  Maximize2,
  Minimize2,
  Link2,
  CheckCircle,
  AlertCircle,
  ZoomIn,
  ZoomOut
} from 'lucide-react';

function ResourcePage() {
  const { id } = useParams();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState(null);
  const [subjectName, setSubjectName] = useState(null);
  const [levelName, setLevelName] = useState(null);
  const [degreeName, setDegreeName] = useState(null);
  const [correction, setCorrection] = useState(null);
  const [originalResource, setOriginalResource] = useState(null);
  const [pdfZoom, setPdfZoom] = useState(100);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('ID de ressource manquant');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // ===== YOUR ORIGINAL WORKING API - STAYS EXACTLY THE SAME =====
        const response = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/resources/${id}?populate[file][populate]=*&populate[subject][populate][level][populate]=degree`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Ressource non trouvée');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data || !data.data) {
          setError('Ressource non trouvée');
          setLoading(false);
          return;
        }
        
        const resourceItem = data.data;
        setResource(resourceItem);
        
        // Get PDF URL
        let fileUrl = null;
        if (resourceItem.file && resourceItem.file.length > 0 && resourceItem.file[0]?.url) {
          const relativeUrl = resourceItem.file[0].url;
          if (relativeUrl.startsWith('http')) {
            fileUrl = relativeUrl;
          } else {
            const cleanUrl = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
            fileUrl = `https://unwavering-luck-2076e754ea.strapiapp.com${cleanUrl}`;
          }
        }
        setPdfUrl(fileUrl);
        
        // Get subject hierarchy
        const subject = resourceItem.subject;
        if (subject) {
          setSubjectName(subject.name);
          const level = subject.level;
          if (level) {
            setLevelName(level.name);
            const degree = level.degree;
            if (degree) {
              setDegreeName(degree.name);
            }
          }
        }
        
        // ===== SEPARATE API CALL FOR CORRECTION =====
        try {
          const correctionRes = await fetch(`https://unwavering-luck-2076e754ea.strapiapp.com/api/resources/${id}?populate=correction&populate=resource`);
          const correctionData = await correctionRes.json();
          
          // The correction is directly on the data object
          if (correctionData.data && correctionData.data.correction) {
            setCorrection(correctionData.data.correction);
          }
          
          if (correctionData.data && correctionData.data.resource) {
            setOriginalResource(correctionData.data.resource);
          }
        } catch (err) {
          console.error('Error fetching correction relations:', err);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  const handleDownload = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const zoomIn = () => {
    setPdfZoom(prev => Math.min(prev + 25, 200));
  };

  const zoomOut = () => {
    setPdfZoom(prev => Math.max(prev - 25, 50));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <Loader size={32} className="text-mint animate-spin" />
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-white/40 text-lg">{error || 'Ressource non trouvée'}</div>
          <Link to="/browse" className="mt-4 inline-block text-mint hover:text-pink transition">Retour à la bibliothèque</Link>
        </div>
      </div>
    );
  }

  const isCorrection = resource.type === 'Correction';
  const hasCorrection = correction !== null;

  return (
    <div className="bg-dark-bg min-h-screen">
      
      {/* Header */}
      <div className="border-b border-white/10 bg-dark-surface/30 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-3 sm:gap-4">
  <img 
    src={logo} 
    alt="ISIMM Archive" 
    className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
    style={{ scale: '1.3' }}
  />
  <div>
    <span className="text-lg sm:text-xl font-mono tracking-tighter text-white">ISIMM<span className="gradient-text">_Archive</span></span>
    <p className="font-mono text-[7px] sm:text-[9px] tracking-wider text-white/40 hidden sm:block">digital library</p>
  </div>
</Link>
              <span className="text-white/20">/</span>
              <Link to="/browse" className="font-mono text-sm text-white/60 hover:text-mint transition">
                Bibliothèque
              </Link>
              <span className="text-white/20">/</span>
              <span className="font-mono text-sm text-white/40 truncate max-w-[300px]">
                {resource.title}
              </span>
            </div>
            <Link to="/browse" className="font-mono text-xs text-white/40 hover:text-mint transition flex items-center gap-1">
              <ArrowLeft size={12} /> Retour
            </Link>
          </div>
        </div>
      </div>

      <div className={`transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-50 bg-dark-bg' : ''}`}>
        <div className={`max-w-7xl mx-auto px-6 py-8 ${isFullscreen ? 'h-full' : ''}`}>
          
          <div className={`grid gap-8 ${isFullscreen ? 'grid-cols-1 h-full' : 'lg:grid-cols-3'}`}>
            
            {/* Left Sidebar - Metadata */}
            {!isFullscreen && (
              <div className="lg:col-span-1">
                <div className="bg-dark-surface/50 border border-white/10 rounded-xl p-6 sticky top-24">
                  
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mint/10 text-mint">
                      <FileText size={16} />
                      <span className="font-mono text-[10px] tracking-wider">{resource.type || 'Document'}</span>
                    </div>
                    
                    {isCorrection && (
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow/10 text-yellow">
                        <CheckCircle size={14} />
                        <span className="font-mono text-[10px] tracking-wider">Correction</span>
                      </div>
                    )}
                  </div>
                  
                  <h1 className="text-xl font-serif text-white mb-4">{resource.title}</h1>
                  
                  <div className="space-y-2 mb-4 pb-4 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <BookOpen size={14} className="text-mint" />
                      <span className="text-white/60 text-sm">Matière: {subjectName || 'Non spécifiée'}</span>
                    </div>
                    {levelName && (
                      <div className="flex items-center gap-2">
                        <GraduationCap size={14} className="text-pink" />
                        <span className="text-white/60 text-sm">Niveau: {levelName}</span>
                      </div>
                    )}
                    {degreeName && (
                      <div className="flex items-center gap-2">
                        <Archive size={14} className="text-yellow" />
                        <span className="text-white/60 text-sm">Département: {degreeName}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/40 flex items-center gap-1"><Calendar size={12} /> Ajouté le</span>
                      <span className="text-white/60 text-xs">{new Date(resource.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/40 flex items-center gap-1"><Clock size={12} /> Modifié le</span>
                      <span className="text-white/60 text-xs">{new Date(resource.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  {/* CORRECTION LINK BUTTON - YELLOW BOX */}
                  {hasCorrection && !isCorrection && correction && (
                    <div className="mt-4 p-3 bg-yellow/10 border border-yellow/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 size={14} className="text-yellow" />
                        <span className="text-yellow text-xs font-mono">CORRECTION DISPONIBLE</span>
                      </div>
                      <Link 
                        to={`/resource/${correction.documentId}`}
                        className="block text-white text-sm hover:text-yellow transition font-mono"
                      >
                        📄 {correction.title}
                      </Link>
                    </div>
                  )}

                  {/* NO CORRECTION MESSAGE */}
                  {!hasCorrection && !isCorrection && resource.type !== 'Correction' && (
                    <div className="mt-4 p-3 bg-dark-card/50 border border-white/10 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertCircle size={14} className="text-white/30" />
                        <span className="text-white/30 text-xs font-mono">AUCUNE CORRECTION DISPONIBLE</span>
                      </div>
                      <p className="text-white/20 text-xs mt-1">
                        Cette ressource ne possède pas encore de correction.
                      </p>
                    </div>
                  )}

                  {/* ORIGINAL DOCUMENT LINK BUTTON - MINT BOX */}
                  {isCorrection && originalResource && (
                    <div className="mt-4 p-3 bg-mint/10 border border-mint/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={14} className="text-mint" />
                        <span className="text-mint text-xs font-mono">DOCUMENT ORIGINAL</span>
                      </div>
                      <Link 
                        to={`/resource/${originalResource.documentId}`}
                        className="block text-white text-sm hover:text-mint transition font-mono"
                      >
                        📄 {originalResource.title} ({originalResource.type})
                      </Link>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3 mt-6">
                    <button
                      onClick={handleDownload}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-mint/10 border border-mint/30 rounded-lg text-mint hover:bg-mint/20 transition font-mono text-xs"
                    >
                      <Download size={14} /> Télécharger
                    </button>
                    <button
                      onClick={handlePrint}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-dark-surface border border-white/10 rounded-lg text-white/60 hover:text-white transition font-mono text-xs"
                    >
                      <Printer size={14} /> Imprimer
                    </button>
                    <button
                      onClick={toggleFullscreen}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-dark-surface border border-white/10 rounded-lg text-white/60 hover:text-white transition font-mono text-xs col-span-2"
                    >
                      <Maximize2 size={14} /> Plein écran
                    </button>
                  </div>
                  
                  <div className="mt-6 p-4 bg-dark-card/50 rounded-lg border border-white/5">
                    <p className="text-white/30 text-xs text-center">
                      Ce document fait partie de l'archive officielle de l'ISIMM.
                      <br />
                      Téléchargement autorisé pour usage personnel.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Right Side - Enhanced PDF Viewer */}
            <div className={`${isFullscreen ? 'h-full' : 'lg:col-span-2'}`}>
              <div className={`bg-dark-surface/50 border border-white/10 rounded-xl overflow-hidden ${isFullscreen ? 'h-full flex flex-col' : ''}`}>
                <div className={`p-4 border-b border-white/10 flex justify-between items-center ${isFullscreen ? 'flex-shrink-0' : ''}`}>
                  <div className="flex items-center gap-2">
                    <Eye size={14} className="text-mint" />
                    <span className="font-mono text-[10px] text-white/40">APERÇU DU DOCUMENT</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {pdfUrl && (
                      <>
                        <div className="flex items-center gap-2 bg-dark-card rounded-lg px-2 py-1">
                          <button
                            onClick={zoomOut}
                            className="text-white/40 hover:text-mint transition p-1"
                            title="Dézoomer"
                          >
                            <ZoomOut size={14} />
                          </button>
                          <span className="text-white/40 text-xs font-mono w-12 text-center">{pdfZoom}%</span>
                          <button
                            onClick={zoomIn}
                            className="text-white/40 hover:text-mint transition p-1"
                            title="Zoomer"
                          >
                            <ZoomIn size={14} />
                          </button>
                        </div>
                        <div className="w-px h-4 bg-white/20" />
                      </>
                    )}
                    {isFullscreen && (
                      <button
                        onClick={toggleFullscreen}
                        className="text-white/40 hover:text-white transition p-1"
                        title="Quitter le plein écran"
                      >
                        <Minimize2 size={14} />
                      </button>
                    )}
                    <div className="text-white/30 text-xs flex items-center gap-2">
                      <FileText size={12} />
                      PDF
                    </div>
                  </div>
                </div>
                
                <div className={`${isFullscreen ? 'flex-1 p-0' : 'p-4'}`}>
                  {pdfUrl ? (
                    <div className="w-full">
                      <div className="bg-dark-card rounded-xl overflow-hidden">
                        <div className="bg-dark-surface/50 p-2 border-b border-white/10 flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="text-mint" />
                            <span className="text-white/40 text-xs font-mono">PDF VIEWER</span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={handleDownload}
                              className="text-white/40 hover:text-mint transition text-xs font-mono"
                            >
                              Télécharger
                            </button>
                            <button
                              onClick={handlePrint}
                              className="text-white/40 hover:text-mint transition text-xs font-mono"
                            >
                              Imprimer
                            </button>
                          </div>
                        </div>
                        <div 
                          className="w-full overflow-auto"
                          style={{ 
                            maxHeight: isFullscreen ? 'calc(100vh - 100px)' : '80vh',
                            minHeight: isFullscreen ? 'calc(100vh - 100px)' : '80vh'
                          }}
                        >
                          <iframe
                            src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH&zoom=${pdfZoom}`}
                            title={resource.title}
                            className="w-full border-0"
                            style={{ 
                              height: isFullscreen ? 'calc(100vh - 100px)' : '80vh',
                              transform: `scale(${pdfZoom / 100})`,
                              transformOrigin: 'top left',
                              width: `${100 / (pdfZoom / 100)}%`
                            }}
                            onError={() => console.log('PDF failed to load')}
                          />
                        </div>
                      </div>
                      <p className="text-white/30 text-xs text-center mt-4">
                        Le PDF s'affiche ci-dessus. Utilisez les contrôles pour naviguer, zoomer ou télécharger.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <div className="w-24 h-24 bg-mint/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <FileText size={48} className="text-mint" />
                      </div>
                      <p className="text-white/40 text-sm mb-4">Aucun fichier PDF disponible</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResourcePage;